import React, { useState, useEffect, useRef } from "react";

const Multiselect = ({
  options = [],
  selected = [],
  onChange,
  placeholder = "Select...",
  disabled = false,
  labelKey = "label",
  valueKey = "value",
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchTerm), 200);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus input on dropdown open
  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  const filteredOptions = options.filter((option) =>
    option[labelKey].toLowerCase().includes(debouncedSearch.toLowerCase())
  );

  const toggleOption = (option) => {
    if (disabled) return;
    const optionValue = option[valueKey];
    const isSelected = selected.includes(optionValue);

    if (isSelected) {
      onChange(selected.filter((val) => val !== optionValue));
    } else {
      onChange([...selected, optionValue]);
    }
  };

  const removeOption = (option) => {
    if (disabled) return;
    const optionValue = option[valueKey];
    onChange(selected.filter((val) => val !== optionValue));
  };

  const highlightMatch = (label) => {
    const parts = label.split(new RegExp(`(${debouncedSearch})`, "gi"));
    return parts.map((part, i) => (
      <span
        key={i}
        className={
          part.toLowerCase() === debouncedSearch.toLowerCase()
            ? "bg-yellow-200"
            : ""
        }
      >
        {part}
      </span>
    ));
  };

  return (
    <div className={`relative w-full ${className}`} ref={containerRef}>
      <div
        className={`flex flex-wrap items-center gap-1 min-h-[2.5rem] px-2 py-1 border rounded-md cursor-pointer bg-white ${
          disabled
            ? "bg-gray-100 cursor-not-allowed"
            : "hover:border-blue-500 border-gray-300"
        }`}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            !disabled && setIsOpen((prev) => !prev);
          }
        }}
      >
        {selected.length === 0 && (
          <span className="text-gray-400 select-none">{placeholder}</span>
        )}
        {selected.map((value) => {
          const option = options.find((opt) => opt[valueKey] === value);
          if (!option) return null;

          return (
            <span
              key={value}
              className="flex items-center bg-blue-100 text-blue-700 rounded px-2 py-0.5 text-sm"
            >
              {option[labelKey]}
              {!disabled && (
                <button
                  type="button"
                  className="ml-1 focus:outline-none"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeOption(option);
                  }}
                  aria-label={`Remove ${option[labelKey]}`}
                >
                  &#10005;
                </button>
              )}
            </span>
          );
        })}

        <input
          ref={inputRef}
          type="text"
          className={`flex-grow border-none focus:ring-0 focus:outline-none text-sm ${
            disabled ? "bg-gray-100 cursor-not-allowed" : ""
          }`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          disabled={disabled}
          aria-label="Search options"
        />
      </div>
      {isOpen && (
        <ul
          className="absolute z-10 w-full max-h-60 overflow-auto mt-1 bg-white border border-gray-300 rounded-md shadow-lg"
          role="listbox"
          tabIndex={-1}
        >
          {filteredOptions.length === 0 && (
            <li className="p-2 text-gray-500 select-none">No options found</li>
          )}
          {filteredOptions.map((option) => {
            const isSelected = selected.some(
              (item) => item[valueKey] === option[valueKey]
            );
            return (
              <li
                key={option[valueKey]}
                className={`cursor-pointer select-none px-3 py-2 hover:bg-blue-100 ${
                  isSelected ? "bg-blue-200 font-semibold" : ""
                }`}
                onClick={() => toggleOption(option)}
                role="option"
                aria-selected={isSelected}
              >
                {highlightMatch(option[labelKey])}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default Multiselect;
